<template>
  <div class="tooth-page">
    <div class="tooth-page__inner">
      <!-- Başlık -->
      <div class="tooth-card tooth-page__header">
        <div class="tooth-page__ident">
          <span class="tooth-page__avatar"><i class="fa-solid fa-tooth" aria-hidden="true"></i></span>
          <div class="min-w-0">
            <h4 class="tooth-page__name mb-0" :title="customerInfo.name">
              {{ customerInfo.name }}
              <font-awesome-icon v-if="customerInfo.customerTypeColor" icon="fa-solid fa-rhombus" class="ml-1 tooth-page__type" :class="customerInfo.customerTypeColor" />
            </h4>
            <small class="text-muted">{{ $t(isEdit ? 'ToothTreatment' : 'NewToothTreatment') }}</small>
          </div>
        </div>
        <b-button variant="outline-secondary" class="tooth-page__back" @click="goBack()">
          <i class="fa fa-arrow-left mr-1" aria-hidden="true"></i>{{ $t('Cancel') }}
        </b-button>
      </div>

      <b-alert v-if="!isAuthorized" show variant="warning" class="tooth-card">
        {{ $t('PermissionNotGranted') }}
      </b-alert>

      <template v-else>
        <Loader v-if="isLoading" :is-visible="isLoading" />

        <template v-else>
          <!-- Kontroller -->
          <div class="tooth-card tooth-page__toolbar">
            <b-form-group class="tooth-page__field mb-0" label-class="tooth-page__label" :label="$t('TreatmentDate')">
              <DatePicker v-model="model.toothTreatment.date" :tag-name="'toothTreatmentDate'" :is-required="true" />
            </b-form-group>

            <b-form-group class="tooth-page__field mb-0" label-class="tooth-page__label" :label="$t('ToothTemplate')" label-for="toothTemplate">
              <b-form-select id="toothTemplate" :key="templateSelectKey" :value="toothTemplate" :options="templateOptions" @change="onTemplateChange" />
            </b-form-group>

            <div class="tooth-page__modes" :class="{ 'tooth-page__modes--active': selectMode }">
              <template v-if="selectMode">
                <span class="tooth-page__modecount">
                  {{ selectedTeeth.length > 0 ? $t('ToothSelectedCount', { count: selectedTeeth.length }) : $t('ToothSelectionHint') }}
                </span>
                <b-button key="add" variant="primary" :disabled="selectedTeeth.length === 0" @click="openServiceModal(selectedTeeth, true)">
                  <i class="fa fa-plus mr-1" aria-hidden="true"></i>{{ selectedTeeth.length === 1 ? $t('AddServiceToSelectedTooth') : $t('AddServiceToSelectedTeeth') }}
                </b-button>
                <b-button key="close" variant="light" @click="closeSelectMode()">
                  <i class="fa fa-times mr-1" aria-hidden="true"></i>{{ $t('CloseSelectionMode') }}
                </b-button>
              </template>
              <!-- Anahtarlar: bkz. ToothChart (FontAwesome <i> -> <svg>). -->
              <b-button v-else key="open" variant="outline-primary" @click="selectMode = true">
                <i class="fa fa-hand-pointer mr-1" aria-hidden="true"></i>{{ $t('SelectionMode') }}
              </b-button>
            </div>
          </div>

          <!-- Diş şeması -->
          <div class="tooth-card tooth-page__chart">
            <ToothChart :template="toothTemplate"
                        :treatment-counts="treatmentCounts"
                        :selected-teeth="selectedTeeth"
                        :highlighted-teeth="highlightedTeeth"
                        :accent-tooth="accentTooth"
                        :select-mode="selectMode"
                        @tooth-click="onToothClick" />
          </div>

          <!-- Eklenen tedaviler, not ve kaydet -->
          <div class="tooth-card tooth-page__records">
            <div v-if="!hasAnyDetail" class="tooth-empty">
              <span class="tooth-empty__icon"><i class="fa-solid fa-tooth" aria-hidden="true"></i></span>
              <span class="tooth-empty__text">{{ $t('ToothTreatmentPlaceholder') }}</span>
            </div>

            <template v-else>
              <ToothTreatmentTable :details="model.toothTreatmentDetails"
                                   :parity-list="parityList"
                                   :show-pricing="showPricing"
                                   @hover-group="teeth => highlightedTeeth = teeth"
                                   @hover-tooth="tooth => accentTooth = tooth"
                                   @delete-tooth="deleteDetail"
                                   @delete-group="deleteGroup" />

              <b-form-group class="mt-4 mb-0" label-class="tooth-page__label" :label="$t('TreatmentNote')" label-for="toothTreatmentNote">
                <textarea id="toothTreatmentNote" v-model="model.toothTreatment.notes" rows="3" class="form-control" :maxlength="treatmentNoteMaxLength"></textarea>
                <small v-if="(model.toothTreatment.notes || '').length >= treatmentNoteMaxLength" class="text-danger">{{ $t('ToothTreatmentMessage') }}</small>
              </b-form-group>

              <div class="tooth-page__foot">
                <b-overlay :show="isSubmitting" spinner-small spinner-variant="primary" class="d-inline-block">
                  <b-button variant="primary" class="px-5" :disabled="isSubmitting" @click="submit()">{{ $t('Save') }}</b-button>
                </b-overlay>
              </div>
            </template>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
  import Loader from '../../../components/shared/Loader'
  import DatePicker from '../../../components/shared/DatePicker'
  import ToothChart from '../../../components/tooth/ToothChart'
  import ToothTreatmentTable from '../../../components/tooth/ToothTreatmentTable'
  import ToothTreatmentService from '../../../components/tooth/ToothTreatmentService'
  import { TOOTH_TEMPLATE, TOOTH_TREATMENT_PRICING_ENABLED, TREATMENT_NOTE_MAX_LENGTH, parseAmount, confirmToothAction } from '../../../components/tooth/toothConfig'
  import { getUniqueString } from '../../../utils/stringExtensions'

  const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
  const isEmptyGuid = value => !value || value === EMPTY_GUID

  import '../../../plugins/vue-modal'
  import '../../../plugins/sweetalert'

  import toothService from '../../../services/tooth'
  import customerService from '../../../services/customer'
  import commonService from '../../../services/common'

  export default {
    name: 'ToothTreatmentEditor',
    components: { Loader, DatePicker, ToothChart, ToothTreatmentTable },
    props: {
      customerId: { type: String, required: true },
      toothTreatmentId: { type: String, default: null }
    },
    data() {
      return {
        isLoading: false,
        isSubmitting: false,
        customerInfo: {},
        toothTemplate: TOOTH_TEMPLATE.permanent,
        selectMode: false,
        templateSelectKey: 0,
        selectedTeeth: [],
        highlightedTeeth: [],
        accentTooth: null,
        currencyList: [],
        parityList: [],
        discountGroup: null,
        // İndirim grubu kararı: null = henüz sorulmadı, '' = reddedildi, "%10"/"10" = kabul edildi.
        customerDiscount: null,
        showPricing: TOOTH_TREATMENT_PRICING_ENABLED,
        treatmentNoteMaxLength: TREATMENT_NOTE_MAX_LENGTH,
        model: {
          toothTreatment: {
            id: null,
            notes: '',
            date: this.$moment().format('DD.MM.YYYY'),
            // Vega'da teklif modülü yok: kilit gösterilmez, var olan değer olduğu gibi korunur.
            createdForPropose: false,
            isPrimary: false,
            customerId: this.customerId
          },
          toothTreatmentDetails: [],
          deletedToothTreatmentDetails: []
        }
      }
    },
    computed: {
      isEdit() {
        return !!(this.toothTreatmentId && this.toothTreatmentId.length > 0)
      },
      isAuthorized() {
        return this.$store.getters['Auth/checkPermission'](this.isEdit ? 'toothtreatment_edit' : 'toothtreatment_new')
      },
      hasAnyDetail() {
        return this.model.toothTreatmentDetails.length > 0
      },
      treatmentCounts() {
        return this.model.toothTreatmentDetails.reduce((counts, detail) => {
          counts[detail.toothNumber] = (counts[detail.toothNumber] || 0) + 1
          return counts
        }, {})
      },
      templateOptions() {
        return [
          { value: TOOTH_TEMPLATE.permanent, text: this.$t('PermanentTeeth') },
          { value: TOOTH_TEMPLATE.primary, text: this.$t('PrimaryTeeth') }
        ]
      }
    },
    methods: {
      getCustomerInfo() {
        customerService.getCustomerInfo(this.customerId).then(response => {
          if (response && response.id == this.customerId) {
            this.customerInfo = response
            document.title = 'Vega - ' + response.name
          }
        })
      },
      getPricingData() {
        if (!this.showPricing) return
        commonService.getCurrencyList().then(response => { this.currencyList = response || [] })
        commonService.getParities().then(response => { this.parityList = response || [] })
        toothService.getCustomerDiscountGroup(this.customerId).then(response => { this.discountGroup = response || null })
      },
      // clientapp her hizmet penceresinde soruyordu; burada editör oturumunda bir kez sorulur.
      ensureDiscountDecision() {
        if (!this.showPricing || !this.discountGroup || this.customerDiscount !== null) return Promise.resolve()

        const group = this.discountGroup
        const discountText = group.isFixedAmount ? `${group.value}` : `%${group.value}`

        return confirmToothAction(this, {
          title: discountText,
          text: `${group.name}: ${this.$t('DiscountGroupWarning')}`,
          confirmText: this.$t('DiscountButton')
        }).then(confirmed => {
          this.customerDiscount = confirmed ? discountText : ''
        })
      },
      getToothTreatment() {
        this.isLoading = true
        toothService.getToothTreatment(this.toothTreatmentId)
          .then(response => {
            if (!response || !response.toothTreatment) {
              this.$toastr.error(this.$t('Error'))
              this.goBack()
              return
            }

            const treatment = response.toothTreatment
            this.model.toothTreatment = {
              id: treatment.id,
              notes: treatment.notes || '',
              // API tarihi "+00:00" ofsetiyle döner; tarayıcı saat dilimine çevirmeden gün alınır.
              date: treatment.date ? this.$moment.parseZone(treatment.date).format('DD.MM.YYYY') : this.$moment().format('DD.MM.YYYY'),
              createdForPropose: !!treatment.createdForPropose,
              isPrimary: !!treatment.isPrimary,
              customerId: treatment.customerId || this.customerId
            }
            this.toothTemplate = treatment.isPrimary ? TOOTH_TEMPLATE.primary : TOOTH_TEMPLATE.permanent
            this.model.toothTreatmentDetails = (response.toothTreatmentDetails || []).map(detail => Object.assign({}, detail, {
              tempId: detail.id,
              // Backend boş hizmet/ürün kimliğini Guid.Empty döner; gruplama ve kayıt için null'a çevrilir.
              serviceId: isEmptyGuid(detail.serviceId) ? null : detail.serviceId,
              productId: isEmptyGuid(detail.productId) ? null : detail.productId
            }))
            this.model.deletedToothTreatmentDetails = []
          })
          .catch(() => {
            this.$toastr.error(this.$t('Error'))
          })
          .finally(() => { this.isLoading = false })
      },
      onToothClick(toothNumber) {
        if (this.selectMode) {
          const index = this.selectedTeeth.indexOf(toothNumber)
          if (index === -1) this.selectedTeeth.push(toothNumber)
          else this.selectedTeeth.splice(index, 1)
          return
        }
        this.openServiceModal([toothNumber], false)
      },
      openServiceModal(teeth, fromSelectMode) {
        if (!teeth || teeth.length === 0) return

        // Seçim modunda aynı anda eklenen hizmetler ortak bir grup kimliği taşır (clientapp ile aynı).
        const groupId = fromSelectMode ? getUniqueString() : null
        const selectedTeeth = teeth.slice()

        this.ensureDiscountDecision().then(() => {
          this.$modal.show(
            ToothTreatmentService,
            {
              customerId: this.customerId,
              selectedTeeth,
              groupId,
              showPricing: this.showPricing,
              currencyList: this.currencyList,
              parityList: this.parityList,
              customerDiscount: this.customerDiscount || '',
              onSubmit: details => this.addDetails(details, fromSelectMode)
            },
            { width: 900, minWidth: 320, minHeight: 300 }
          )
        })
      },
      addDetails(details, fromSelectMode) {
        details.forEach(detail => this.model.toothTreatmentDetails.push(detail))
        if (fromSelectMode) this.closeSelectMode()
      },
      closeSelectMode() {
        this.selectMode = false
        this.selectedTeeth = []
      },
      markDeleted(detail) {
        if (detail.id && this.model.deletedToothTreatmentDetails.indexOf(detail.id) === -1)
          this.model.deletedToothTreatmentDetails.push(detail.id)
      },
      deleteDetail(detail) {
        this.markDeleted(detail)
        this.model.toothTreatmentDetails = this.model.toothTreatmentDetails.filter(d => d !== detail)
        this.accentTooth = null
      },
      deleteGroup(group) {
        group.items.forEach(detail => this.markDeleted(detail))
        this.model.toothTreatmentDetails = this.model.toothTreatmentDetails.filter(d => group.items.indexOf(d) === -1)
        this.highlightedTeeth = []
      },
      onTemplateChange(newTemplate) {
        if (newTemplate === this.toothTemplate) return

        if (!this.hasAnyDetail) {
          this.applyTemplate(newTemplate)
          return
        }

        // Onay gelene kadar seçim kutusu eski şablona döner (yeniden çizilerek).
        this.templateSelectKey++

        confirmToothAction(this, { text: this.$t('ToothTemplateChangeWarning'), confirmText: this.$t('Yes') }).then(confirmed => {
          if (confirmed) {
            this.model.toothTreatmentDetails.forEach(detail => this.markDeleted(detail))
            this.model.toothTreatmentDetails = []
            this.applyTemplate(newTemplate)
          }
        })
      },
      applyTemplate(template) {
        this.toothTemplate = template
        this.model.toothTreatment.isPrimary = template === TOOTH_TEMPLATE.primary
        this.closeSelectMode()
      },
      buildPayload() {
        const treatment = this.model.toothTreatment
        const date = this.$moment(treatment.date, ['DD.MM.YYYY', this.$moment.ISO_8601], true)

        const toothTreatment = {
          notes: treatment.notes,
          date: date.isValid() ? date.format('YYYY-MM-DD') : null,
          createdForPropose: treatment.createdForPropose,
          isPrimary: this.toothTemplate === TOOTH_TEMPLATE.primary,
          customerId: this.customerId
        }
        if (this.isEdit) toothTreatment.id = treatment.id

        return {
          toothTreatment,
          toothTreatmentDetails: this.model.toothTreatmentDetails.map(detail => {
            const row = {
              staffId: detail.staffId,
              toothNumber: detail.toothNumber,
              toothNotes: detail.toothNotes,
              serviceId: detail.serviceId || null,
              productId: detail.productId || null,
              entityType: detail.entityType,
              price: parseAmount(detail.price),
              currencyId: detail.currencyId || 0,
              quantity: parseAmount(detail.quantity || 1).toString(),
              userInputDiscount: detail.userInputDiscount ? detail.userInputDiscount.toString() : '',
              useCreditPrice: !!detail.useCreditPrice,
              isDeleted: false,
              rowTotal: parseAmount(detail.rowTotal).toFixed(2),
              groupId: detail.groupId || null
            }
            // Yeni satırlarda id gönderilmez (Guid.Empty); boş metin Guid'e çevrilemez.
            if (detail.id) row.id = detail.id
            return row
          }),
          deletedToothTreatmentDetails: this.model.deletedToothTreatmentDetails.slice()
        }
      },
      submit() {
        const payload = this.buildPayload()

        if (!payload.toothTreatment.date || payload.toothTreatmentDetails.length === 0) {
          this.$toastr.error(this.$t('ValidationMessage.EmptyAndValidateError'))
          return
        }

        this.isSubmitting = true
        const request = this.isEdit ? toothService.editToothTreatment(payload) : toothService.insertToothTreatment(payload)

        request
          .then(result => {
            if (result === true) {
              this.$toastr.success(this.$t('Success'))
              this.goBack()
            } else {
              this.$toastr.error(this.$t('Error'))
            }
          })
          .catch(() => { this.$toastr.error(this.$t('Error')) })
          .finally(() => { this.isSubmitting = false })
      },
      goBack() {
        this.$router.push({ name: 'customerSummary.toothTreatments', params: { customerId: this.customerId } })
      }
    },
    mounted() {
      if (!this.isAuthorized) return

      this.getCustomerInfo()
      this.getPricingData()
      if (this.isEdit) this.getToothTreatment()
    }
  }
</script>

<style scoped>
  .tooth-page {
    min-height: 100vh;
    padding: 24px 16px 40px;
    background: #eff7f8;
    font-family: 'Poppins', sans-serif;
  }

  .tooth-page__inner {
    max-width: 1240px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .tooth-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(55, 73, 72, .06);
    padding: 20px 24px;
  }

  .tooth-page__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .tooth-page__ident {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }

  .min-w-0 {
    min-width: 0;
  }

  .tooth-page__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    border-radius: 14px;
    font-size: 20px;
    color: #089bab;
    background: #e3f4f6;
  }

  .tooth-page__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    color: #374948;
  }

  .tooth-page__type {
    font-size: 14px;
  }

  .tooth-page__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px 24px;
  }

  .tooth-page__field {
    flex: 0 1 220px;
    min-width: 180px;
  }

  .tooth-page >>> .tooth-page__label {
    padding-bottom: 4px;
    margin-bottom: 4px;
    font-size: 13px;
    color: #777d74;
  }

  .tooth-page__modes {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    padding: 6px;
    border-radius: 12px;
    transition: background-color .15s ease;
  }

  .tooth-page__modes--active {
    background: #e3f4f6;
  }

  .tooth-page__modecount {
    padding: 0 8px;
    font-size: 13px;
    font-weight: 500;
    color: #089bab;
  }

  .tooth-page__chart {
    padding: 24px 16px;
  }

  .tooth-empty {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border: 1px dashed #bfe3e7;
    border-radius: 14px;
    background: #f6fbfc;
    color: #374948;
  }

  .tooth-empty__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #089bab;
    background: #e3f4f6;
  }

  .tooth-empty__text {
    font-size: 14px;
  }

  .tooth-page__foot {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }

  @media (max-width: 767.98px) {
    .tooth-page {
      padding: 12px 8px 24px;
    }

    .tooth-card {
      padding: 16px;
      border-radius: 14px;
    }

    .tooth-page__modes {
      margin-left: 0;
      width: 100%;
    }

    .tooth-page__field {
      flex: 1 1 100%;
    }

    .tooth-page__back {
      padding-left: 10px;
      padding-right: 10px;
    }

    .tooth-page__chart {
      padding: 16px 8px;
    }
  }
</style>
